import { Controller } from "@hotwired/stimulus"

// Keeps the reader where they were when a control re-renders the page it sits
// on instead of navigating away — a cookie-backed preference toggle
// (LogbookSystemEntriesController, ThemesController, LocalesController,
// TodoSnoozedTimecardsController and every one that follows the same shape).
//
// Those controllers write a cookie and redirect_back, which is a fresh GET as
// far as the browser is concerned: the scroll container is rebuilt at offset 0
// and a reader halfway down a long detail screen is thrown to the top. Turbo
// only restores scroll on restoration visits (back/forward), never on the
// advance visit a redirect produces, so nothing else recovers this.
//
// Attach to the form:
//
//   <form data-controller="preserve-scroll"> ... </form>
//
// It records the offset on submit and re-applies it when the control reconnects
// on the re-rendered page. The record is keyed to the path it was taken on and
// consumed once, so a submit that genuinely navigates elsewhere leaves the new
// page at the top. Works with data-turbo="false" too, since turbo:load fires on
// a plain full page load as well.
//
// This is NOT the seam for a scroll container inside a Turbo Frame that
// re-renders on sort/filter/paginate — that is scroll-memory, which saves
// continuously rather than once per submit.
const STORAGE_KEY = "cup_preserve_scroll"

export default class extends Controller {
  connect() {
    this.remember = this.remember.bind(this)
    this.restore = this.restore.bind(this)
    this.element.addEventListener("submit", this.remember)
    // Not connect() itself: Turbo renders the new body (Stimulus connects) and
    // only THEN resets the scroll, so an offset applied on connect is wiped a
    // moment later. turbo:load lands after that reset, and fires on a plain full
    // page load too — which is what a data-turbo="false" toggle produces.
    document.addEventListener("turbo:load", this.restore)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.remember)
    document.removeEventListener("turbo:load", this.restore)
  }

  remember() {
    const container = this.scrollContainer()
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        path: this.currentPath(),
        container: container ? container.scrollTop : 0,
        window: window.scrollY,
      })
    )
  }

  restore() {
    const saved = sessionStorage.getItem(STORAGE_KEY)
    if (!saved) return
    sessionStorage.removeItem(STORAGE_KEY)

    let offsets
    try {
      offsets = JSON.parse(saved)
    } catch {
      return
    }
    if (offsets.path !== this.currentPath()) return

    // The container reaches its full height only once the re-rendered content
    // has laid out, so the first attempt can land while the offset is still
    // clamped. Keep re-applying for up to a second — NOT "until the height stops
    // changing for one frame", which gives up after two: a lazy <turbo-frame>
    // with an src (the upload detail's timecards panel) leaves the height flat
    // for many frames and only then jumps, which is exactly the case that needs
    // the retry. A reader who scrolls in the meantime wins immediately.
    let attempts = 0
    let abandoned = false
    const abandon = () => {
      abandoned = true
    }
    const events = ["wheel", "touchstart", "keydown"]
    events.forEach((name) => document.addEventListener(name, abandon, { once: true, passive: true }))
    const stop = () => events.forEach((name) => document.removeEventListener(name, abandon))

    const apply = () => {
      if (abandoned || !this.element.isConnected) return stop()

      const container = this.scrollContainer()
      if (container) container.scrollTop = offsets.container
      if (offsets.window) window.scrollTo(0, offsets.window)

      const reached = container ? Math.abs(container.scrollTop - offsets.container) <= 1 : offsets.container === 0
      if (reached || (attempts += 1) >= 60) return stop()
      requestAnimationFrame(apply)
    }
    // Apply once synchronously so the reader never sees the top of the page for
    // a frame, then let the loop top it up as late content lands.
    apply()
  }

  currentPath() {
    return window.location.pathname + window.location.search
  }

  // The nearest scrollable ancestor, falling back to the layout's <main> — the
  // app shell's own controls (theme, locale) sit in the top bar, OUTSIDE the
  // region that scrolls, so walking up from them alone finds nothing and the
  // page they were pressed on would still jump.
  scrollContainer() {
    let node = this.element.parentElement
    while (node && node !== document.body) {
      if (this.scrollable(node)) return node
      node = node.parentElement
    }
    const main = document.querySelector("main")
    return main && this.scrollable(main) ? main : null
  }

  scrollable(node) {
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY !== "auto" && overflowY !== "scroll") return false
    return node.scrollHeight > node.clientHeight
  }
}
